import { Core } from "@faire/design-tokens";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { Chevron } from "@faire/web--source/slate/icons/foundation/Chevron";
import { FormInputLabel } from "@faire/web--source/slate/Input";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import * as React from "react";
import { useTheme } from "styled-components";

import {
  MobileChevronWrapper,
  MobileInnerWrapper,
  MobileSelectWrapper,
  StyledSelect,
} from "../styles";

import { IOptionMixin } from "./types";

interface MobileDropdownProps<T extends string> {
  borderless?: boolean;
  className?: string;
  dataTestId?: string;
  disabled?: boolean;
  errorMessage?: string;
  options: IOptionMixin<T>[];
  helper?: string;
  id: string;
  label?: string;
  onOpen?: () => void;
  onSelect: (optionValue: T) => void;
  placeholder?: HTMLInputElement["placeholder"];
  showErrorMessage: boolean;
  showErrorState: boolean;
  value?: T;
}

export const MobileDropdown = <T extends string>({
  borderless,
  className,
  dataTestId,
  disabled,
  errorMessage,
  options,
  helper,
  id,
  label,
  onOpen,
  onSelect,
  placeholder,
  showErrorMessage,
  showErrorState,
  value,
}: MobileDropdownProps<T>) => {
  const theme = useTheme();
  const isValueInOptions = options.some((option) => option.value === value);
  const validValue = isValueInOptions ? value : "";
  const handleSelectClick = React.useCallback(
    (e: React.MouseEvent<HTMLSelectElement>) => {
      onOpen?.();
      e.preventDefault();
      e.stopPropagation();
    },
    [onOpen]
  );

  return (
    <MobileSelectWrapper className={className}>
      {isNotUndefined(label) ? (
        <FormInputLabel
          $typography={{
            ...(theme?.input.label ?? {}),
            color: showErrorState
              ? "error"
              : theme?.input.label?.color ?? Core.text.default,
          }}
        >
          {label}
        </FormInputLabel>
      ) : null}
      <MobileInnerWrapper
        $borderless={borderless}
        $showErrorState={showErrorState}
        $disabled={disabled}
      >
        <MobileChevronWrapper>
          <Chevron
            direction="down"
            color={disabled ? Core.icon.disabled : Core.icon.default}
            width="8"
            height="8"
            strokeWidth={2}
          />
        </MobileChevronWrapper>
        <StyledSelect
          id={id}
          data-test-id={dataTestId}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore used for tests, even though it's not a valid prop on select in some React versions.
          placeholder={placeholder}
          disabled={disabled}
          onClick={handleSelectClick}
          onChange={(e) => onSelect(e.target.value as T)}
          value={validValue || ""}
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {options.map((option) => (
            <option
              data-test-id={`dropdownItem_${option.value}`}
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </StyledSelect>
      </MobileInnerWrapper>

      {helper && !showErrorMessage ? (
        <>
          <Spacer size={4} />
          <Typography
            {...{
              color: Core.text.subdued,
              ...(theme?.input.helper ?? {}),
            }}
          >
            {helper}
          </Typography>
        </>
      ) : null}
      {showErrorMessage ? (
        <>
          <Spacer size={4} />
          <Typography variant="labelSansRegular" color={Core.text.critical}>
            {errorMessage}
          </Typography>
        </>
      ) : null}
    </MobileSelectWrapper>
  );
};
